import { Box, ButtonProps, FormHelperText, Grid } from '@mui/material';
import { SendCodeButton } from '@/components/general/buttons/SendCodeButton/SendCodeButton';
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { GridSize } from '@/types/types';
import { useConfigStore } from '@/store/configStore/configStore';

import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface WithSendCodeProps {
    hasSendCode: boolean;
    children?: ReactNode;
    onSendCode: () => Promise<boolean | { secondsLeft: number }>;
    codeType?: 'email' | 'phone';
    codeFormInput?: ReactElement;
    buttonBreakpoint?: GridSize;
    errorMessage?: string;
    buttonLabel?: string;
    buttonStyle?: ButtonProps;
    // Start the timer at a specific number of seconds (e.g., restored after refresh)
    initialRefreshSeconds?: number;
}

export const WithSendCode = (props: WithSendCodeProps) => {
    const { website_2fa_email_resend_code_timeout, website_sms_resend_code_timeout } =
        useConfigStore((state) => ({
            website_2fa_email_resend_code_timeout: state.website_2fa_email_resend_code_timeout,
            website_sms_resend_code_timeout: state.website_sms_resend_code_timeout,
        }));

    const timerStartTime =
        props.codeType === 'phone'
            ? website_sms_resend_code_timeout
            : website_2fa_email_resend_code_timeout;

    const [refreshInterval, setRefreshInterval] = useState<number>(
        props.initialRefreshSeconds && props.initialRefreshSeconds > 0
            ? props.initialRefreshSeconds
            : 0,
    );
    const [restartToken, setRestartToken] = useState<number>(0);

    const hasFormInput = Boolean(props.codeFormInput);
    const hasAutoWidthButton = !hasFormInput && !props.buttonBreakpoint;

    const buttonBreakpoint = props.buttonBreakpoint ?? 7;

    let buttonSmValue = hasAutoWidthButton ? ('auto' as const) : 12;
    let inputSmValue = 12;

    if (hasFormInput) {
        if (buttonBreakpoint >= 12 || buttonBreakpoint <= 0) {
            buttonSmValue = 12;
            inputSmValue = 12;
        } else {
            buttonSmValue = buttonBreakpoint;
            inputSmValue = 12 - buttonBreakpoint;
        }
    }

    const buttonGridSize = {
        xs: 12,
        sm: buttonSmValue,
    };

    const inputGridSize = {
        xs: 12,
        sm: inputSmValue,
    };

    // If initial refresh seconds comes from the parent later (restored), reflect it
    useEffect(() => {
        if (props.initialRefreshSeconds && props.initialRefreshSeconds > 0) {
            setRefreshInterval(props.initialRefreshSeconds);
            setRestartToken((x) => x + 1);
        }
    }, [props.initialRefreshSeconds]);

    const handleSendCodeButtonClick = async () => {
        const codeSent = await props.onSendCode();

        if (typeof codeSent === 'object' && codeSent !== null && 'secondsLeft' in codeSent) {
            setRefreshInterval(codeSent.secondsLeft);
            setRestartToken((x) => x + 1);
            return false;
        }

        if (codeSent === true) {
            setRefreshInterval(timerStartTime);
            setRestartToken((x) => x + 1);
        }

        return codeSent;
    };

    return (
        <>
            <Grid container>
                <Grid size={12}>{props.children}</Grid>
            </Grid>
            {props.hasSendCode && (
                <Grid
                    container
                    sx={{ pt: { xs: 0, sm: 1.75 } }}
                    spacing={{ xs: 3, sm: 2.5 }}
                    data-testid="sendcode-input"
                >
                    <Grid size={buttonGridSize} sx={{ flexShrink: 0 }}>
                        <SendCodeButton
                            onClick={handleSendCodeButtonClick}
                            refreshInterval={refreshInterval}
                            restartToken={restartToken}
                        >
                            {props.buttonLabel ? props.buttonLabel : undefined}
                        </SendCodeButton>
                    </Grid>
                    {hasFormInput && <Grid size={inputGridSize}>{props.codeFormInput}</Grid>}
                </Grid>
            )}
            {props.errorMessage && (
                <Grid container>
                    <Grid size={12}>
                        <FormHelperText error>
                            <Box
                                component={ErrorOutlineIcon}
                                sx={{
                                    verticalAlign: 'middle',
                                    mr: 0.5,
                                    fontSize: '1rem',
                                    color: 'error.main',
                                }}
                            />
                            {props.errorMessage}
                        </FormHelperText>
                    </Grid>
                </Grid>
            )}
        </>
    );
};
